import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import axios from "axios"
import { AXIOS_API_INSTANCE, RENTAL_CONTRACT_COLOR_MAP, RENTAL_STATUS_FRIENDLY_NAMES } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, CircularProgress, IconButton } from "@mui/material"
import Grid from "@mui/material/Grid2"
import Scheduler from "@aldabil/react-scheduler";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from 'dayjs';
import { useTheme } from "@emotion/react"

const hash_func = (object) => {
    //set variable hash as 0
    var hash = 0;
    var string = JSON.stringify(object)
    // if the length of the string is 0, return 0
    if (string.length === 0) return hash;
    for (var i = 0; i < string.length; i++) {
        var ch = string.charCodeAt(i);
        hash = ((hash << 5) - hash) + ch;
        hash = hash & hash;
    }
    return hash;
}

export default function ContractEditDateModal({
    contract,
    open,
    rentalItems,
    closeModal
}) {
    const theme = useTheme()
    const { enqueueSnackbar } = useSnackbar();
    const [my_contract,] = React.useState(contract)
    const [startDate, setStartDate] = React.useState(dayjs.utc(contract.estimated_start_date))
    const [endDate, setEndDate] = React.useState(dayjs.utc(contract.estimated_end_date))
    const [contracts, setContracts] = React.useState([])
    const [isLoading, setIsLoading] = React.useState(false)

    const getContracts = (item) => {
        const filterVar1 = {
            'and': [{ 'rental_item_id.eq': item.rental_item_id },
            { 'rental_contract_id.ne': item.rental_contract_id },
            {
                'or': [{ 'estimated_start_date.lt': dayjs.utc(contract.estimated_end_date) }, { 'start_date.lt': dayjs.utc(contract.estimated_end_date) }]
            },
            {
                'or': [{ 'status.eq': "RESERVED" }, { 'status.eq': "CHECKED_OUT" }, { 'status.eq': "CHECKING_IN" }]
            }]
        }
        const filterVar2 = {
            'and': [{ 'rental_item_id.eq': item.rental_item_id },
            { 'rental_contract_id.ne': item.rental_contract_id },
            {
                'or': [{ 'estimated_end_date.gt': dayjs.utc(contract.estimated_start_date) }]
            },
            {
                'or': [{ 'status.eq': "RESERVED" }, { 'status.eq': "CHECKED_OUT" }, { 'status.eq': "CHECKING_IN" }]
            }]
        }
        return [AXIOS_API_INSTANCE.get("rentalcontractitems", {
            params: {
                page_num: 0,
                page_size: 10,
                order_by: 'estimated_start_date',
                order_by_direction: 'desc',
                filters: JSON.stringify(filterVar1),
            },
        }), AXIOS_API_INSTANCE.get("rentalcontractitems", {
            params: {
                order_by: 'estimated_end_date',
                order_by_direction: 'asc',
                filters: JSON.stringify(filterVar2),
            },
        })]
    }

    const getAllContracts = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            axios
                .all(my_contract.rental_contract_items.map((item) => getContracts(item)).flat())
                .then((contracts) => {
                    console.log(contracts)
                    let events = []
                    contracts.map((x) => x.data.data).flat().forEach(contract => {
                        const start = contract.start_date ? dayjs.utc(contract.start_date).local() : dayjs.utc(contract.estimated_start_date).local()
                        const end = contract.end_date ? dayjs.utc(contract.end_date).local() : dayjs.utc(contract.estimated_end_date).local()
                        events.push(
                            {
                                rental_item_id: contract.rental_item_id,
                                event_id: contract.rental_contract_id,
                                title: `${rentalItems[contract.rental_item_id].unique_name} - ${RENTAL_STATUS_FRIENDLY_NAMES[contract.status]}`,
                                start: start.toDate(),
                                end: (end < start ? start.add(1, 'h') : end).toDate(),
                                color: theme.palette[RENTAL_CONTRACT_COLOR_MAP[contract.status] || 'primary'].main,
                                editable: false,
                                deletable: false,
                                draggable: false
                            })
                    })

                    setContracts(events)
                    setIsLoading(false)
                })
        )
    }

    const save = (event) => {
        const my_contract_object = {
            action: 'UPDATE',
            rental_contract: {
                estimated_start_date: startDate,
                estimated_end_date: endDate
            }
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.post(
                `rentalcontracts/${my_contract.id}/check_out`,
                my_contract_object
            ).then((rentalContract) => {
                enqueueSnackbar('Successfully updated contract', { variant: 'success' })
                closeModal(rentalContract.data.data)
            }).catch((error) => {
                enqueueSnackbar(`Failed to update contract <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        if (open === true) {
            getAllContracts()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    React.useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate])

    const item_min_times = {}
    const item_max_times = {}
    for (const item of my_contract.rental_contract_items) {
        item_min_times[item.id] = "None"
        item_max_times[item.id] = "None"
        const my_events = contracts.filter((contract) => contract.rental_item_id === item.rental_item_id)
        if (my_events.find((contract) => contract.start < endDate && contract.end > startDate)) {
            item_min_times[item.id] = "OVERLAPPING"
            item_max_times[item.id] = "OVERLAPPING"
        } else {
            item_min_times[item.id] = (my_events.filter((contract) => contract.end < dayjs().utc(item.estimated_start_date)).map((contract) => contract.end).sort().pop() ?? dayjs.utc()).toString()
            item_max_times[item.id] = (my_events.filter((contract) => contract.start > dayjs().utc(item.estimated_end_date)).map((contract) => contract.start).sort().pop() ?? "None").toString()
        }
    }

    const this_event = {
        event_id: `this${contract.id}`,
        title: `THIS CONTRACT`,
        start: startDate.toDate(),
        end: endDate.toDate(),
        color: 'red',
        editable: false,
        deletable: false,
        draggable: false
    }

    const calendar_events = contracts.concat([this_event])

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'xl'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>
                    Reschedule
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <Grid container direction="column">
                                <Grid sx={{ pt: 1 }} size="grow">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Grid container spacing={1}>
                                            <Grid size="grow">
                                                <MobileDateTimePicker
                                                    value={startDate}
                                                    label="Start Time"
                                                    onChange={(newValue) => {
                                                        setStartDate(newValue)
                                                    }}
                                                    onAccept={(newValue) => {
                                                        if (newValue > endDate) {
                                                            setEndDate(newValue.add(15, 'minutes'))
                                                        }
                                                    }}
                                                    minutesStep={15}
                                                    onError={console.log}
                                                    renderInput={(params) => <TextField {...params} />}
                                                    minDateTime={dayjs().utc().ceil('minutes', 15)}
                                                    format="YYYY/MM/DD hh:mm a"
                                                    mask="____/__/__ __:__ _M"
                                                />
                                            </Grid>
                                            <Grid size="grow">
                                                <MobileDateTimePicker
                                                    value={endDate}
                                                    label="End Time"
                                                    onChange={(newValue) => {
                                                        setEndDate(newValue)
                                                    }}
                                                    minutesStep={15}
                                                    onError={console.log}
                                                    renderInput={(params) => <TextField {...params} />}
                                                    minDateTime={startDate}
                                                    format="YYYY/MM/DD hh:mm a"
                                                    mask="____/__/__ __:__ _M"
                                                />
                                            </Grid>
                                        </Grid>
                                    </LocalizationProvider>
                                </Grid>
                                {my_contract.rental_contract_items.map((item) =>
                                    <Grid size="grow">
                                        <Grid container>
                                            <Grid size="auto">
                                                {(item?.image_path !== undefined && item?.image_path !== null && item?.image_path !== "") ?
                                                    <Box component="img" alt={"Rental Item"} src={`/customerimages/client/${item.client_id}/rentalitems/${item.id}/${item.image_path}`} sx={{ maxHeight: '40px', maxWidth: '40px' }} />
                                                    :
                                                    <Box component="img" alt={"Rental Item"} src={`/noimage.png`} sx={{ maxHeight: '40px', maxWidth: '40px' }} />
                                                }
                                            </Grid>
                                            <Grid size="grow">
                                                <span><b>{rentalItems[item.rental_item_id].unique_name}</b> ({item_min_times[item.id]} - {item_max_times[item.id]})</span>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid maxWidth={'100%'} size={8}>
                            {isLoading &&
                                < Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress />
                                </Box>
                            }
                            {!isLoading &&
                                <Scheduler
                                    key={hash_func(calendar_events)}
                                    // selectedDate={selectedDate}
                                    view={"month"}
                                    events={calendar_events}
                                    // onSelectedDateChange={updateSelectedDate} // TODO: Update package version once it is fixed to add support for this
                                    // onConfirm={handleConfirm}
                                    viewerExtraComponent={(fields, event) => <Button href={`/Contracts/${event.event_id}`}>View Contract</Button>}
                                />
                            }
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>)
    );
}
